import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, AppWindow, Globe, FileUp } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useApplications } from "@/hooks/useApplications";
import { Application } from "@/types/application";
import { PppcIdentifierType } from "@/types/pppc";
import { AppIcon } from "@/components/applications/AppIcon";
import { PppcImportDialog, PppcImportResult } from "./PppcImportDialog";
import { cn } from "@/lib/utils";

export type PppcTargetMode = "catalog" | "external";

export interface PppcTargetValue {
  mode: PppcTargetMode;
  catalogAppId?: string;
  appName: string;
  identifier: string;
  identifierType: PppcIdentifierType;
}

export interface PppcTargetErrors {
  catalogAppId?: string;
  appName?: string;
  identifier?: string;
}

interface PppcTargetStepProps {
  value: PppcTargetValue;
  onChange: (next: PppcTargetValue) => void;
  errors?: PppcTargetErrors;
  /** When true, the wizard was opened from an app context — lock the target. */
  locked?: boolean;
  /**
   * Optional bulk-import handler. When provided, the step renders an
   * "Import from .mobileconfig" button that opens the parser dialog. The
   * wizard owns the merge logic for the resulting choices/code-requirement.
   */
  onImport?: (result: PppcImportResult) => void;
  /** Current code requirement, passed through to the import dialog for conflict checks. */
  currentCodeRequirement?: string;
  /** Current choices, passed through to the import dialog for the keep-existing merge. */
  currentChoices?: import("@/types/pppc").PppcChoicesMap;
}

export const PppcTargetStep = ({
  value,
  onChange,
  errors,
  locked = false,
  onImport,
  currentCodeRequirement,
  currentChoices,
}: PppcTargetStepProps) => {
  const { applications, loading } = useApplications();
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const macApps = useMemo(
    () =>
      applications.filter(
        (a) => (a.platform || "").toLowerCase() === "macos" && !!a.bundleId,
      ),
    [applications],
  );

  const filteredApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return macApps;
    return macApps.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.bundleId ?? "").toLowerCase().includes(q),
    );
  }, [macApps, search]);

  const selectCatalogApp = (app: Application) => {
    onChange({
      mode: "catalog",
      catalogAppId: app.id,
      appName: app.name,
      identifier: app.bundleId ?? "",
      identifierType: "bundleID",
    });
  };

  const setMode = (mode: PppcTargetMode) => {
    if (mode === "catalog") {
      onChange({
        mode,
        catalogAppId: undefined,
        appName: "",
        identifier: "",
        identifierType: "bundleID",
      });
    } else {
      onChange({
        mode,
        catalogAppId: undefined,
        appName: value.appName,
        identifier: value.identifier,
        identifierType: value.identifierType || "bundleID",
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Target App</h2>
          <p className="text-sm text-muted-foreground mt-1">
            PPPC policies pre-approve macOS privacy services for a specific app.
            Choose from your catalog or describe an app installed outside Smplify.
          </p>
        </div>
        {onImport && !locked && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setImportOpen(true)}
            className="shrink-0"
          >
            <FileUp className="h-3.5 w-3.5 mr-1.5" />
            Import .mobileconfig
          </Button>
        )}
      </div>

      <RadioGroup
        value={value.mode}
        onValueChange={(v) => !locked && setMode(v as PppcTargetMode)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        disabled={locked}
      >
        <Label
          htmlFor="pppc-mode-catalog"
          className={cn(
            "flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors",
            value.mode === "catalog" && "border-primary bg-primary/5",
            locked && "opacity-60 cursor-not-allowed",
          )}
        >
          <RadioGroupItem id="pppc-mode-catalog" value="catalog" className="mt-1" />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <AppWindow className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Pick from my catalog</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Bundle ID is auto-filled from the app's metadata.
            </p>
          </div>
        </Label>

        <Label
          htmlFor="pppc-mode-external"
          className={cn(
            "flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors",
            value.mode === "external" && "border-primary bg-primary/5",
            locked && "opacity-60 cursor-not-allowed",
          )}
        >
          <RadioGroupItem id="pppc-mode-external" value="external" className="mt-1" />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">External app</span>
            </div>
            <p className="text-xs text-muted-foreground">
              For apps installed manually or via another tool.
            </p>
          </div>
        </Label>
      </RadioGroup>

      {value.mode === "catalog" && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search macOS apps in your catalog…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                disabled={locked}
              />
            </div>

            {errors?.catalogAppId && (
              <p className="text-xs text-destructive">{errors.catalogAppId}</p>
            )}

            <ScrollArea className="h-[320px] pr-2">
              {loading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Loading apps…
                </p>
              ) : filteredApps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No macOS apps found in your catalog.
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredApps.map((app) => {
                    const selected = value.catalogAppId === app.id;
                    return (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => !locked && selectCatalogApp(app)}
                        disabled={locked}
                        className={cn(
                          "w-full text-left flex items-center gap-3 p-3 rounded-md border transition-colors",
                          selected
                            ? "border-primary bg-primary/5"
                            : "hover:border-primary/40",
                        )}
                      >
                        <AppIcon
                          iconSrc={app.icon}
                          appName={app.name}
                          className="h-9 w-9"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{app.name}</p>
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {app.bundleId}
                          </p>
                        </div>
                        {selected && <Badge variant="secondary">Selected</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {value.mode === "external" && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label className="text-sm">Identifier type</Label>
              <RadioGroup
                value={value.identifierType}
                onValueChange={(v) =>
                  onChange({
                    ...value,
                    identifierType: v as PppcIdentifierType,
                    identifier: "",
                  })
                }
                className="flex gap-4 mt-2"
                disabled={locked}
              >
                <Label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="bundleID" />
                  <span className="text-sm">Bundle ID</span>
                </Label>
                <Label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="path" />
                  <span className="text-sm">Path</span>
                </Label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="pppc-identifier" className="text-sm">
                {value.identifierType === "path" ? "App path" : "Bundle ID"}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pppc-identifier"
                value={value.identifier}
                onChange={(e) =>
                  onChange({ ...value, identifier: e.target.value })
                }
                placeholder={
                  value.identifierType === "path"
                    ? "/Applications/Zoom.app"
                    : "us.zoom.xos"
                }
                className="mt-1 font-mono text-sm"
                aria-invalid={!!errors?.identifier}
                disabled={locked}
              />
              {errors?.identifier && (
                <p className="text-xs text-destructive mt-1">{errors.identifier}</p>
              )}
            </div>

            <div>
              <Label htmlFor="pppc-app-name" className="text-sm">
                App name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pppc-app-name"
                value={value.appName}
                onChange={(e) => onChange({ ...value, appName: e.target.value })}
                placeholder="Zoom"
                className="mt-1"
                maxLength={80}
                aria-invalid={!!errors?.appName}
                disabled={locked}
              />
              {errors?.appName && (
                <p className="text-xs text-destructive mt-1">{errors.appName}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {onImport && (
        <PppcImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          currentChoices={currentChoices ?? {}}
          currentCodeRequirement={currentCodeRequirement ?? ""}
          preferIdentifier={value.identifier || undefined}
          onImport={onImport}
        />
      )}
    </div>
  );
};

const BUNDLE_ID_RE = /^[A-Za-z0-9.\-]+$/;

export const validatePppcTarget = (value: PppcTargetValue): PppcTargetErrors => {
  const errors: PppcTargetErrors = {};

  if (value.mode === "catalog") {
    if (!value.catalogAppId) {
      errors.catalogAppId = "Select an app from your catalog.";
    }
    if (!value.identifier) {
      errors.identifier = "Selected app is missing a bundle ID.";
    }
    return errors;
  }

  // External
  const name = value.appName.trim();
  if (name.length < 1 || name.length > 80) {
    errors.appName = "App name is required (1–80 characters).";
  }

  const id = value.identifier.trim();
  if (value.identifierType === "bundleID") {
    if (id.length < 3 || !id.includes(".") || !BUNDLE_ID_RE.test(id)) {
      errors.identifier =
        "Bundle ID must contain a dot and only letters, numbers, dots and hyphens.";
    }
  } else {
    if (!id.startsWith("/") || !id.endsWith(".app")) {
      errors.identifier = "Path must start with / and end with .app";
    }
  }

  return errors;
};
